/**
 * AppPaywallController
 *
 * Thin controller that renders PlanExplorerDialog as a persistent,
 * non-dismissible gate for accounts with no active trial and no subscription.
 *
 * Mount in two places:
 *   1. Globally in App.tsx (no props needed) — covers every authenticated
 *      "orphan" route (/dashboard, /workspaces, /user-settings, /tools, …).
 *   2. Scoped inside DashboardLayout (scoped + accessGranted + disabled props)
 *      to preserve the owner-aware, context-loading-guarded paywall for
 *      /p/* and /workspace/* routes.
 *
 * Because the global instance excludes /p/* and /workspace/* via
 * isGlobalGateRoute(), and the scoped instance only lives on those routes,
 * exactly one instance is active per route — no double dialogs.
 */

import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import PlanExplorerDialog from "@/components/dashboard/hub/PlanExplorerDialog";
import { TrialExpiredPaywall, buildPlanSummary } from "@/components/shared/plan-usage/TrialExpiredPaywall";
import { useBilling } from "@/hooks/useBilling";
import { useTrialUsageStats } from "@/hooks/useTrialUsageStats";
import { useAuth } from "@/lib/contexts/AuthContext";
import { useSubscription } from "@/lib/contexts/SubscriptionContext";
import { getPaymentLink, getPlanById } from "@/lib/constants/pricing";
import { logPaywallShown, usePaywallScope } from "@/lib/observability/paywall";
import { isTrialActivatingFresh, markTrialActivating } from "@/lib/utils/trialActivation";
import { track } from "@/lib/utils/track";

type AppPaywallControllerProps = {
  /**
   * true when mounted inside DashboardLayout.
   * The scoped instance trusts its mount (always a gated route) and skips the
   * global route classifier; it relies on accessGranted / disabled for exemptions.
   */
  scoped?: boolean;
  /**
   * When true, suppress the auto-block entirely.
   * Use this for the property-owner-covers-members exemption
   * (propertyOwnerHasAccess in DashboardLayout).
   */
  accessGranted?: boolean;
  /**
   * Suppresses the *automatic* hard-block gate only: context still loading,
   * demo property, workspace aggregate mode, or any other condition that
   * should prevent the persistent paywall from auto-opening. Does NOT
   * suppress manual opens via openPricingModal() — an explicit upgrade CTA
   * must always be able to open the dialog.
   */
  disabled?: boolean;
};

/**
 * Routes that the global (non-scoped) instance should gate.
 * Returns true for every authenticated "orphan" route that is NOT already
 * covered by DashboardLayout's scoped instance.
 *
 * Exclusions (returns false):
 *   - Public / auth / checkout routes
 *   - Shared magic-link routes (/s/)
 *   - Onboarding steps (/onboarding)
 *   - Workspace creation wizard (/workspace/new)
 *   - Root redirect (/)
 *   - DashboardLayout-owned prefixes (/p/, /workspace/) — scoped instance covers these
 */
function isGlobalGateRoute(pathname: string): boolean {
  const excluded = [
    "/auth",
    "/verify",
    "/me",
    "/change-email",
    "/oauth-callback",
    "/checkout-success",
    "/agent-verify",
    "/agent-complete",
    "/mcp-connect",
    "/accept-invite",
    "/sign-in",
    "/sign-up",
    "/s/",
    "/onboarding",
    "/onboarding/plan",
    "/workspace/new",
    "/workspace-v2/new",
    "/add-website/legacy",
  ];

  if (pathname === "/") return false;

  // DashboardLayout-owned prefixes — scoped instance handles these
  if (pathname.startsWith("/p/") || pathname.startsWith("/workspace/")) return false;

  return !excluded.some((prefix) => pathname.startsWith(prefix));
}

/** Session-scoped: the trial-expired paywall is shown again in a new session. */
const TRIAL_VIEW_ONLY_KEY = "humblytics:trial-view-only:v1";

function readTrialViewOnly(): boolean {
  try {
    return sessionStorage.getItem(TRIAL_VIEW_ONLY_KEY) === "1";
  } catch {
    return false;
  }
}

function writeTrialViewOnly() {
  try {
    sessionStorage.setItem(TRIAL_VIEW_ONLY_KEY, "1");
  } catch {
    // Storage blocked: the paywall closes for this mount only.
  }
}

export function AppPaywallController({
  scoped = false,
  accessGranted = false,
  disabled = false,
}: AppPaywallControllerProps) {
  const { token, user, logout } = useAuth();
  const billing = useBilling();
  const {
    isPricingModalOpen,
    closePricingModal,
    currentPlan,
    pricingModalReason,
    usedTrial,
    trialEndsAt,
    isAgentPlan,
    isLoading: subscriptionLoading,
  } = useSubscription();
  const location = useLocation();
  const navigate = useNavigate();
  const paywallScope = usePaywallScope();
  const trialStats = useTrialUsageStats();
  const [isRedirecting, setIsRedirecting] = useState(false);
  const [searchParams] = useSearchParams();

  // Suppress the paywall while a Stripe checkout is in flight or the activation
  // webhook hasn't landed yet. isTrialActivatingFresh() reads localStorage and
  // expires after 5 min, giving the webhook time to resolve.
  const activating = !!searchParams.get("stripe_session_id") || isTrialActivatingFresh();

  // Account is blocked if billing has loaded and no access path exists. Free
  // start and Agents are prepaid, not a Stripe subscription: never blocked.
  const accountBlocked =
    !isAgentPlan &&
    !subscriptionLoading &&
    !billing.isLoading &&
    !!token &&
    !billing.subscriptionExists &&
    !billing.hasActiveTrial &&
    !billing.hasActiveSubscription;

  // Determine whether this instance should be active on the current route
  const routeActive = scoped
    ? !activating
    : isGlobalGateRoute(location.pathname) && !activating;

  // A spent trial can step past the paywall into the view-only app for the
  // rest of the browser session ("View my data"). Accounts that never trialed
  // stay behind the plan picker: they have no history to view.
  const [trialViewOnly, setTrialViewOnly] = useState(readTrialViewOnly);

  // Hard auto-block: persistent gate, user must start trial at Stripe
  const autoBlock =
    routeActive && accountBlocked && !accessGranted && !disabled && !(usedTrial && trialViewOnly);

  // Manual open from upgrade CTAs: always dismissible, and never suppressed by
  // `disabled` — that flag exists to prevent the *automatic* hard-block from
  // flashing while context loads or on demo/aggregate routes, not to swallow
  // an explicit openPricingModal() call from an in-page upgrade CTA (e.g. a
  // limit-reached "Add page"/"New Funnel" button). Without this, those CTAs
  // silently no-op on /p/* and /workspace/* routes whenever disabled is true.
  const manualOpen = routeActive && isPricingModalOpen;

  const open = autoBlock || manualOpen;

  // A spent trial gets the "tracking paused" paywall (Paper "Home · Trial
  // expired paywall"): one plan, one action, data reported as saved. Accounts
  // that never trialed keep the full plan picker.
  const showTrialExpired = autoBlock && usedTrial && !isPricingModalOpen;

  const loggedExpired = useRef(false);
  useEffect(() => {
    if (showTrialExpired && !loggedExpired.current) {
      logPaywallShown({
        surface: "trial_expired_paywall",
        trigger: "forced_paywall",
        persistent: true,
        pathname: location.pathname,
        scope: paywallScope,
      });
    }
    loggedExpired.current = showTrialExpired;
  }, [showTrialExpired, location.pathname, paywallScope]);

  if (!open) return null;

  if (showTrialExpired) {
    const plan = getPlanById("business");
    if (plan) {
      return (
        <TrialExpiredPaywall
          open
          planName={plan.name}
          monthlyPrice={plan.monthlyPrice}
          planSummary={buildPlanSummary("business")}
          trialEndsAt={trialEndsAt}
          stats={{
            sessions: trialStats.sessions_n,
            funnels: trialStats.funnels_n,
            heatmaps: trialStats.heatmaps_n,
          }}
          isRedirecting={isRedirecting}
          onReactivate={() => {
            setIsRedirecting(true);
            // Trial already spent: the direct (no-trial) monthly link, matching
            // the "$79/mo" on the button.
            track("stripe_checkout_initiated", token, {
              plan: plan.id,
              billing: "monthly",
              is_trial: false,
              trigger: "trial_expired_paywall",
            });
            markTrialActivating();
            window.location.href = getPaymentLink(plan, false, true, user?.email, user?.userId);
          }}
          onViewData={() => {
            track("paywall_view_data_clicked", token, { trigger: "trial_expired_paywall" });
            writeTrialViewOnly();
            setTrialViewOnly(true);
          }}
          onLogout={() => {
            track("paywall_logout_clicked", token, { trigger: "trial_expired_paywall" });
            logout();
            navigate("/auth");
          }}
        />
      );
    }
  }

  return (
    <PlanExplorerDialog
      open={open}
      persistent={autoBlock}
      currentPlanId={currentPlan ?? undefined}
      limitReason={pricingModalReason}
      onOpenChange={(next) => {
        if (!next) closePricingModal();
      }}
    />
  );
}
